import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatDate, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Plus, Check, X, Eye, CalendarDays, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/* Detail drill-down: requests can only be approved/rejected from inside this
 * dialog, so the reviewer always sees the full context before deciding. */
function ReviewDialog({ kind, item, empName, allItems, onClose, onDecided, endpoint }) {
    const [context, setContext] = useState(null);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (!item) return;
        setContext(null); setProfile(null);
        api.get(`/hris/employees/${item.employee_id}`).then((r) => setProfile(r.data)).catch(() => {});
        if (kind === "leave") {
            api.get(`/leave/balances/${item.employee_id}`).then((r) => setContext({ balances: r.data })).catch(() => setContext({}));
        } else {
            api.get("/timekeeping/attendance", { params: { start_date: item.work_date, end_date: item.work_date } })
                .then((r) => setContext({ attendance: (r.data.items || []).find((a) => a.employee_id === item.employee_id) || null }))
                .catch(() => setContext({}));
        }
    }, [item, kind]);

    if (!item) return null;

    const history = (allItems || []).filter((x) => x.employee_id === item.employee_id && x.id !== item.id).slice(0, 5);
    const monthOt = kind === "overtime"
        ? (allItems || []).filter((x) => x.employee_id === item.employee_id && x.status === "approved" && (x.work_date || "").slice(0, 7) === (item.work_date || "").slice(0, 7))
            .reduce((s, x) => s + (x.hours || 0), 0)
        : 0;

    const decide = async (decision) => {
        let reason;
        if (decision === "rejected") {
            reason = window.prompt("Rejection reason?");
            if (!reason) return;
        }
        try {
            await api.post(`${endpoint}/${item.id}/approve`, { decision, reason });
            toast.success(`Marked ${decision}`);
            onDecided();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const Row = ({ label, children }) => (
        <div className="flex justify-between gap-3 py-1 border-b border-border/60 last:border-0 text-sm">
            <span className="text-muted-foreground text-xs pt-0.5">{label}</span>
            <span className="text-right">{children ?? "—"}</span>
        </div>
    );

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {kind === "leave" ? <CalendarDays className="size-4 text-primary" /> : <Clock className="size-4 text-primary" />}
                        {kind === "leave" ? "Leave request" : "Overtime request"} — verify
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 max-h-[65vh] overflow-auto pr-1">
                    <Surface className="p-3">
                        <div className="text-sm font-semibold">{empName}</div>
                        {profile && <div className="text-xs text-muted-foreground">{profile.employee_no} · {profile.position || "—"} · {profile.department || profile.department_name || ""}</div>}
                        <div className="mt-2">
                            {kind === "leave" ? (
                                <>
                                    <Row label="Type"><span className="capitalize">{String(item.leave_type || "").replaceAll("_", " ")}</span></Row>
                                    <Row label="Dates"><span className="font-mono">{item.start_date} → {item.end_date}</span></Row>
                                    <Row label="Days"><b>{item.days}</b></Row>
                                    <Row label="Reason">{item.reason}</Row>
                                    {item.requires_medcert && (
                                        <Row label="Medical certificate">
                                            {item.medcert_uploaded
                                                ? <a className="text-primary underline text-xs" href={`/api/timekeeping/leaves/${item.id}/medical-certificate`} target="_blank" rel="noreferrer" data-testid="view-medcert">View certificate ({item.medcert_filename})</a>
                                                : <span className="text-destructive text-xs font-semibold">⚠ Not uploaded — approval is blocked (accredited clinic only)</span>}
                                        </Row>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Row label="Work date"><span className="font-mono">{item.work_date}</span></Row>
                                    <Row label="Hours filed"><b>{item.hours}</b></Row>
                                    <Row label="Reason">{item.reason}</Row>
                                </>
                            )}
                            <Row label="Filed">{item.created_at ? formatDateTime(item.created_at) : "—"}</Row>
                            <Row label="Status"><StatusBadge value={item.status} /></Row>
                        </div>
                    </Surface>

                    {/* Verification context */}
                    {kind === "leave" && context?.balances && (
                        <Surface className="p-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Leave balances (verify before approving)</div>
                            <div className="grid grid-cols-2 gap-1.5">
                                {(context.balances.balances || []).filter((b) => b.applicable).map((b) => (
                                    <div key={b.leave_type} className={`border rounded-md px-2 py-1.5 text-xs ${b.leave_type === item.leave_type ? "border-primary bg-primary/5" : "border-border"}`}>
                                        <span className="capitalize">{(b.leave_type || "").replace(/_/g, " ")}</span>
                                        <div className="font-mono font-semibold">{b.remaining_days} <span className="font-normal text-muted-foreground">left of {b.entitlement_days} · used {b.used_days}</span></div>
                                    </div>
                                ))}
                                {!(context.balances.balances || []).length && <div className="text-xs text-muted-foreground col-span-2">No balance data.</div>}
                            </div>
                        </Surface>
                    )}
                    {kind === "overtime" && context && (
                        <Surface className="p-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Attendance on {item.work_date} (verify before approving)</div>
                            {context.attendance ? (
                                <>
                                    <Row label="Time in"><span className="font-mono text-xs">{context.attendance.time_in ? formatDateTime(context.attendance.time_in) : "—"}</span></Row>
                                    <Row label="Time out"><span className="font-mono text-xs">{context.attendance.time_out ? formatDateTime(context.attendance.time_out) : "—"}</span></Row>
                                    <Row label="Hours worked"><b>{(context.attendance.hours_worked || 0).toFixed(2)}</b></Row>
                                </>
                            ) : <div className="text-xs text-destructive">⚠ No attendance record on this date — verify before approving OT.</div>}
                            <div className="text-xs text-muted-foreground mt-1.5">Approved OT this month: <b className="font-mono">{monthOt.toFixed(1)} h</b></div>
                        </Surface>
                    )}

                    {history.length > 0 && (
                        <Surface className="p-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Recent {kind} requests by this employee</div>
                            {history.map((h) => (
                                <div key={h.id} className="flex justify-between text-xs py-0.5">
                                    <span className="font-mono">{kind === "leave" ? `${h.start_date} → ${h.end_date} · ${h.days}d ${h.leave_type}` : `${h.work_date} · ${h.hours}h`}</span>
                                    <StatusBadge value={h.status} />
                                </div>
                            ))}
                        </Surface>
                    )}
                </div>

                <DialogFooter>
                    {item.status === "submitted" ? (
                        <div className="flex gap-2">
                            <button onClick={() => decide("rejected")} className="h-9 px-4 rounded-md bg-destructive text-white text-sm flex items-center gap-1" data-testid={`reject-${kind}-${item.id}`}><X className="size-4" /> Reject</button>
                            <button onClick={() => decide("approved")} className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm flex items-center gap-1" data-testid={`approve-${kind}-${item.id}`}><Check className="size-4" /> Approve</button>
                        </div>
                    ) : <span className="text-xs text-muted-foreground">Already {item.status} — read-only.</span>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ApprovalsList({ endpoint, kind, columns }) {
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState({});
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({});
    const [review, setReview] = useState(null);
    const [medcert, setMedcert] = useState(null);
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");

    const load = async () => {
        const [r, emp] = await Promise.all([api.get(endpoint), api.get("/hris/employees", { params: { limit: 500 } })]);
        const m = {}; (emp.data.items || []).forEach((e) => m[e.id] = `${e.last_name}, ${e.first_name}`);
        setEmployees(m);
        setItems(r.data.items || []);
    };
    useEffect(() => { load(); }, [endpoint]);

    // ?focus=<id> (from the bell, a notification or the approvals inbox): open that request for review, once.
    const focusId = searchParams.get("focus");
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (!focusId || focused || !items.length) return;
        const hit = items.find((i) => i.id === focusId);
        if (hit) { setReview(hit); setFocused(true); }
    }, [focusId, items, focused]);

    const isSick = kind === "leave" && (form.leave_type || "").toLowerCase() === "sick";
    const submit = async () => {
        if (isSick && !medcert) return toast.error("A medical certificate is required for sick leave (company-accredited clinic only).");
        try {
            const { data: created } = await api.post(endpoint, form);
            if (isSick && medcert && created?.id) {
                const fd = new FormData();
                fd.append("file", medcert);
                await api.post(`/timekeeping/leaves/${created.id}/medical-certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success("Submitted");
            setOpen(false);
            setForm({});
            setMedcert(null);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const shown = statusFilter ? items.filter((i) => i.status === statusFilter) : items;

    return (
        <>
            <Surface className="p-3 mb-3 flex items-center gap-2">
                <select className="h-9 px-2 rounded-md border border-border text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid={`${kind}-status-filter`}>
                    <option value="">All statuses</option>
                    {["submitted", "approved", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <span className="text-xs text-muted-foreground">Open a request to verify details before approving.</span>
                <div className="ml-auto">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid={`new-${kind}-btn`}>
                                <Plus className="size-4" /> New {kind}
                            </button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>New {kind} request</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div>
                                    <Label>Employee</Label>
                                    <EmployeePicker value={form.employee_id || ""} onChange={(v) => setForm({ ...form, employee_id: v })} testId={`${kind}-employee`} />
                                </div>
                                {columns.map((c) => (
                                    <div key={c.key}>
                                        <Label>{c.label}</Label>
                                        <Input type={c.type || "text"} value={form[c.key] || ""} onChange={(e) => setForm({ ...form, [c.key]: c.type === "number" ? Number(e.target.value) : e.target.value })} />
                                    </div>
                                ))}
                                {isSick && (
                                    <div className="border border-amber-300 bg-amber-50 rounded-md p-2.5" data-testid="hr-medcert-box">
                                        <div className="text-xs font-semibold text-amber-900">🩺 Medical certificate — REQUIRED for sick leave</div>
                                        <div className="text-[11px] text-amber-800 mt-0.5 mb-1.5">Must be issued by a <b>company-accredited clinic</b>; otherwise it will not be honored.</div>
                                        <input type="file" accept=".pdf,image/*" onChange={(e) => setMedcert(e.target.files?.[0] || null)} className="w-full text-xs" />
                                        {medcert && <div className="text-[11px] text-emerald-700 mt-1">✓ {medcert.name}</div>}
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid={`submit-${kind}-btn`}>Submit</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </Surface>
            <TableWrap>
                <thead>
                    <tr>
                        <th>Employee</th>
                        {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {shown.map((it) => (
                        <tr key={it.id} data-row-id={it.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => setReview(it)} data-testid={`${kind}-row-${it.id}`}>
                            <td>{employees[it.employee_id] || it.employee_id?.slice(0, 8)}</td>
                            {columns.map((c) => <td key={c.key}>{it[c.key]}</td>)}
                            <td><StatusBadge value={it.status} /></td>
                            <td>
                                <button onClick={(e) => { e.stopPropagation(); setReview(it); }}
                                    className={`h-7 px-2 text-xs rounded-md flex items-center gap-1 ${it.status === "submitted" ? "bg-primary text-white" : "border border-border"}`}
                                    data-testid={`review-${kind}-${it.id}`}>
                                    <Eye className="size-3" /> {it.status === "submitted" ? "Review" : "View"}
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!shown.length && <tr><td colSpan={4 + columns.length}><Empty title={`No ${kind} requests`} /></td></tr>}
                </tbody>
            </TableWrap>

            {review && (
                <ReviewDialog
                    kind={kind}
                    item={review}
                    empName={employees[review.employee_id] || review.employee_id}
                    allItems={items}
                    endpoint={endpoint}
                    onClose={() => setReview(null)}
                    onDecided={() => { setReview(null); load(); }}
                />
            )}
        </>
    );
}

export function LeavesPage() {
    return (
        <div>
            <PageHeader title="Leaves" subtitle="Leave filing and approval workflow. Open a request to verify balances and history before deciding." />
            <ApprovalsList
                endpoint="/timekeeping/leaves"
                kind="leave"
                columns={[
                    { key: "leave_type", label: "Type" },
                    { key: "start_date", label: "From", type: "date" },
                    { key: "end_date", label: "To", type: "date" },
                    { key: "days", label: "Days", type: "number" },
                    { key: "reason", label: "Reason" },
                ]}
            />
        </div>
    );
}

export function OvertimePage() {
    return (
        <div>
            <PageHeader title="Overtime" subtitle="OT filing and approval workflow. OT is not payable unless approved — verify attendance in the drill-down first." />
            <ApprovalsList
                endpoint="/timekeeping/overtime"
                kind="overtime"
                columns={[
                    { key: "work_date", label: "Date", type: "date" },
                    { key: "hours", label: "Hours", type: "number" },
                    { key: "reason", label: "Reason" },
                ]}
            />
        </div>
    );
}
